/**
 * Poisoned-op handling for the PowerSync upload connector.
 *
 * 2026-06-06 incident: a broken RLS helper made one defect_types op fail with
 * `operator does not exist: text = uuid` (42883). The connector rethrew, the
 * batch retried every ~5s for 21h, and sync wedged for the user. Discarding
 * ("dead-lettering") poisoned ops keeps sync flowing; the payload is parked
 * locally AND phoned home (report-dead-letter intent → /api/dead-letters →
 * email) so the dev can recover the data centrally with supabaseAdmin.
 * Spec: docs/superpowers/specs/2026-06-06-powersync-dead-letters-design.md
 */
import type { AbstractPowerSyncDatabase, CrudEntry } from '@powersync/web'
import { Capacitor } from '@capacitor/core'
import { logPsLifecycle } from './lifecycle-log'

export const RETRY_BUDGET_MS = 60 * 60 * 1000
// Single shared watch entry, not per-tab: PowerSync web coordinates upload
// across tabs (navigator locks / shared worker), so only one connector
// drains the one FIFO queue at a time and the queue head is the same
// everywhere. Native is single-document. If that coordination assumption
// ever breaks, the worst case is a poisoned op staying past the 1h budget
// (fail-open) — never a premature discard of a healthy op, because the
// exceeded check requires the SAME txKey to have held the watch the whole
// time. Wall-clock based: clock jumps can delay (backwards) or hasten
// (forwards) the discard; acceptable for a soft 1h budget on the secondary
// discard path.
const WATCH_KEY = 'zs-crud-retry-watch'

// Schema-drift codes: a freshly deployed SPA can upload a new column before
// its DB migration ran. These heal on their own — never discard immediately
// (the retry budget still catches them if they persist past an hour).
const SCHEMA_DRIFT_CODES = new Set(['42703', '42P01'])

export interface PgErrorLike { code?: string; message?: string }

function errorCode(err: unknown): string {
  const code = (err as PgErrorLike | null)?.code
  return typeof code === 'string' ? code : ''
}

/**
 * Postgres error classes that can never succeed on retry: 22xxx (data
 * exception), 23xxx (integrity constraint), 42xxx (syntax/access — includes
 * 42501 RLS denial and 42883). PG codes are exactly 5 chars, so PostgREST's
 * own PGRST… codes never match.
 */
export function isFatalPgError(err: unknown): boolean {
  const code = errorCode(err)
  if (code.length !== 5 || SCHEMA_DRIFT_CODES.has(code)) return false
  return /^(22|23|42)/.test(code)
}

/**
 * True when the server actually judged the op's content (PostgREST error with
 * a code). Timeouts (CrudOpTimeoutError) and network failures have no code —
 * by definition they cannot be poisoned ops, so they never consume the budget.
 */
export function hasServerVerdict(err: unknown): boolean {
  return errorCode(err).length > 0
}

interface RetryWatch { key: number; since: number }

function readWatch(): RetryWatch | null {
  try {
    const raw = window.localStorage.getItem(WATCH_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as RetryWatch
    return typeof parsed?.key === 'number' && typeof parsed?.since === 'number' ? parsed : null
  } catch {
    return null
  }
}

/** Start the budget clock for this transaction; repeated failures keep the original `since`. */
export function noteRetryFailure(txKey: number): void {
  try {
    const watch = readWatch()
    if (!watch || watch.key !== txKey) {
      window.localStorage.setItem(WATCH_KEY, JSON.stringify({ key: txKey, since: Date.now() }))
    }
  } catch { /* localStorage unavailable — budget disabled, ops just retry */ }
}

export function retryBudgetExceeded(txKey: number): boolean {
  const watch = readWatch()
  return !!watch && watch.key === txKey && Date.now() - watch.since > RETRY_BUDGET_MS
}

export function clearRetryWatch(): void {
  try { window.localStorage.removeItem(WATCH_KEY) } catch { /* noop */ }
}

/**
 * Park every op of a poisoned transaction in the localOnly crud_dead_letters
 * table and phone each one home via a report-dead-letter intent. The caller
 * then calls tx.complete() to drop the ops from the upload queue.
 */
export async function deadLetterTransaction(
  db: AbstractPowerSyncDatabase,
  crud: CrudEntry[],
  err: PgErrorLike,
): Promise<void> {
  const discardedAt = new Date().toISOString()
  // Load the intent worker once for the whole transaction, not once per op.
  // Same lazy, cycle-avoiding dynamic import (connector -> dead-letters ->
  // intent-worker -> powersync/client), just hoisted: a multi-op transaction
  // used to fire one identical import() per op.
  const workerModule = crud.length ? import('@/lib/intent-worker') : null
  const code = typeof err.code === 'string' ? err.code : ''
  const message = String(err.message ?? '').slice(0, 1000)
  for (const op of crud) {
    // For PATCH ops, opData is the changed-columns delta, not a full row
    // snapshot — recovery applies it on top of current server state.
    const payload = JSON.stringify(op.opData ?? {})
    // Replay guard: if tx.complete() throws after a successful park (e.g.
    // native SQLite "database is locked" code 5), PowerSync re-uploads the
    // same poisoned tx and re-enters here. clientId is a per-device-unique,
    // never-reused replay key, so a re-park reuses the existing row and skips
    // the INSERT — no duplicate local rows. The intent below is keyed by the
    // same id (db.put upsert), so a replayed enqueue overwrites rather than
    // duplicating, which also dedupes the phone-home email.
    const existing = await db.getOptional<{ id: string }>(
      'SELECT id FROM crud_dead_letters WHERE client_op_id = ?',
      [op.clientId],
    )
    let deadLetterId: string
    if (existing) {
      deadLetterId = existing.id
    } else {
      deadLetterId = crypto.randomUUID()
      await db.execute(
        `INSERT INTO crud_dead_letters
           (id, op_table, client_op_id, op_type, row_id, payload, error_code, error_message, discarded_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [deadLetterId, op.table, op.clientId, String(op.op), op.id, payload, code, message, discardedAt],
      )
      logPsLifecycle('crud-discard', `${op.op} ${op.table} ${op.id} code=${code}`)
    }
    // Fire-and-forget; once enqueued the intent queue is durable, and a
    // discard always happens online (it IS a server response), so the fast
    // tick inside enqueueIntentAndRun fires.
    void workerModule!
      .then(({ enqueueIntentAndRun }) =>
        enqueueIntentAndRun('report-dead-letter', {
          deadLetterId,
          opTable: op.table,
          opType: String(op.op),
          rowId: op.id,
          payload,
          errorCode: code,
          errorMessage: message,
          discardedAt,
          // process.env form (not import.meta.env): this module is reachable
          // from the server tsconfig, which has no vite/client types. The
          // define plugin replaces both token forms with the same value.
          buildId: process.env.VITE_BUILD_ID || 'dev',
          platform: Capacitor.getPlatform(),
        }, { id: deadLetterId }),
      )
      .catch(() => { /* diagnostics must never throw into the upload path */ })
  }
}
